import { AsyncSubject, Observable } from 'rxjs';

export interface RequestCheckerOptions<T> {
  interval: number;
  timeout: number;
  request$: Observable<T>;
  callback: (response: T) => boolean;
  failedAttempts: number;
  tryImmediately?: boolean;
}

export class RequestChecker<T> {
  private readonly responseSubject = new AsyncSubject<T>();

  public get response$() {
    return this.responseSubject.asObservable();
  }

  public get promise() {
    return this.responseSubject.toPromise();
  }

  private startTime: number;
  private started = false;
  private failedCount = 0;

  constructor(private readonly options: Readonly<RequestCheckerOptions<T>>) {}

  start() {
    if (this.started) {
      return;
    }

    this.started = true;
    this.startTime = Date.now();

    if (this.options.tryImmediately) {
      this.handle();
    } else {
      this.waitAndHandle();
    }
  }

  private handle = () => {
    this.options.request$.subscribe(
      (response) => {
        if (
          this.options.callback(response) ||
          Date.now() - this.startTime >= this.options.timeout
        ) {
          this.responseSubject.next(response);
          this.responseSubject.complete();
        } else {
          this.waitAndHandle();
        }
      },
      (error: Error) => {
        window.zbm?.reportError(error);

        if (
          Date.now() - this.startTime >= this.options.timeout ||
          ++this.failedCount >= this.options.failedAttempts
        ) {
          this.responseSubject.error(
            new Error(`Response timed out with ${error.message}
${error.stack}`),
          );
        } else {
          this.waitAndHandle();
        }
      },
    );
  };

  private waitAndHandle = () => setTimeout(this.handle, this.options.interval);
}
